import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Field, GalleryInput, GalleryTextarea } from '@/components/forms/GalleryFormPrimitives';
import { ActionButton, SectionHeader } from './VisualAtoms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

/**
 * Admin modal: create an external artist (and optionally send intake).
 *
 * Props:
 *   open, onClose, exhibitionId, onCreated(artist)
 */
export default function AddExternalArtistModal({ open, onClose, exhibitionId, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    artist_name: '',
    email: '',
    location: '',
    main_medium_style: '',
    main_social_media: '',
    portfolio_link: '',
    artist_bio: '',
  });
  const [errors, setErrors] = useState({});
  const [createdArtist, setCreatedArtist] = useState(null);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeLink, setIntakeLink] = useState('');

  const update = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) {
      setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    }
  };

  const close = () => {
    if (busy || intakeBusy) return;
    setForm({
      artist_name: '', email: '', location: '', main_medium_style: '',
      main_social_media: '', portfolio_link: '', artist_bio: '',
    });
    setErrors({});
    setCreatedArtist(null);
    setIntakeLink('');
    onClose?.();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const errs = {};
    if (!form.artist_name.trim()) errs.artist_name = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    else if (!isValidEmail(form.email)) errs.email = 'Invalid email';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/artists`, {
        ...form,
        exhibition_id: exhibitionId,
      });
      setCreatedArtist(res.data.artist);
      onCreated?.(res.data.artist);
      if (!res.data.created) {
        toast.message('Existing artist returned (matched by email).');
      } else {
        toast.success('External artist created.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create artist.');
    } finally {
      setBusy(false);
    }
  };

  const sendIntake = async () => {
    if (!createdArtist?.id) return;
    setIntakeBusy(true);
    try {
      const res = await axios.post(`${API}/admin/artists/${createdArtist.id}/intake-link`, {
        exhibition_id: exhibitionId,
        send_email: true,
        mode: 'full',
        frontend_origin: window.location.origin,
      });
      setIntakeLink(res.data.link);
      if (res.data.email_sent) toast.success('Intake email sent.');
      else toast.success('Intake link generated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send intake.');
    } finally {
      setIntakeBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/55 backdrop-blur-[2px] z-[100]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={close}
          />
          <motion.aside
            className="fixed right-0 top-0 bottom-0 z-[101] w-full sm:w-[520px] bg-[#070707] border-l border-[#1a1a1a] overflow-y-auto"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="add-external-artist-modal"
          >
            <header className="sticky top-0 z-10 bg-[#070707]/95 backdrop-blur-sm border-b border-[#1a1a1a] px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">External Artist</p>
                <h2 className="font-outfit text-xl font-semibold text-white mt-0.5">
                  Add Artist
                </h2>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                data-testid="close-add-external"
                className="w-8 h-8 border border-[#222] text-[#888] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="p-5">
              {!createdArtist ? (
                <form onSubmit={submit} className="space-y-5" data-testid="add-external-artist-form">
                  <SectionHeader
                    icon={UserPlus}
                    title="Profile"
                    helper="Create an artist profile not attached to any Open Call submission."
                  />
                  <Field label="ARTIST NAME" required error={errors.artist_name}>
                    <GalleryInput
                      data-testid="ext-artist-name"
                      type="text"
                      value={form.artist_name}
                      onChange={(e) => update('artist_name', e.target.value)}
                      error={errors.artist_name}
                    />
                  </Field>
                  <Field label="EMAIL" required error={errors.email}>
                    <GalleryInput
                      data-testid="ext-artist-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      error={errors.email}
                    />
                  </Field>
                  <Field label="LOCATION">
                    <GalleryInput
                      type="text"
                      value={form.location}
                      onChange={(e) => update('location', e.target.value)}
                    />
                  </Field>
                  <Field label="MEDIUM / STYLE">
                    <GalleryInput
                      type="text"
                      value={form.main_medium_style}
                      onChange={(e) => update('main_medium_style', e.target.value)}
                    />
                  </Field>
                  <Field label="SOCIAL">
                    <GalleryInput
                      type="text"
                      value={form.main_social_media}
                      onChange={(e) => update('main_social_media', e.target.value)}
                    />
                  </Field>
                  <Field label="PORTFOLIO">
                    <GalleryInput
                      type="url"
                      value={form.portfolio_link}
                      onChange={(e) => update('portfolio_link', e.target.value)}
                    />
                  </Field>
                  <Field label="ARTIST BIO">
                    <GalleryTextarea
                      rows={3}
                      value={form.artist_bio}
                      onChange={(e) => update('artist_bio', e.target.value)}
                    />
                  </Field>

                  <div className="flex gap-2 pt-2">
                    <ActionButton
                      type="submit"
                      variant="primary"
                      disabled={busy}
                      icon={busy ? Loader2 : UserPlus}
                      data-testid="ext-artist-create-btn"
                    >
                      {busy ? 'Creating…' : 'Create Artist'}
                    </ActionButton>
                  </div>
                </form>
              ) : (
                <div className="space-y-5" data-testid="add-external-artist-success">
                  <SectionHeader
                    icon={UserPlus}
                    title="Artist Created"
                    helper={createdArtist.artist_name}
                  />
                  <p className="text-xs font-mono text-[#aaa]">
                    {createdArtist.email}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <ActionButton
                      variant="primary"
                      onClick={sendIntake}
                      disabled={intakeBusy}
                      icon={intakeBusy ? Loader2 : Send}
                      data-testid="ext-artist-send-intake"
                    >
                      {intakeBusy ? 'Sending…' : (intakeLink ? 'Resend Intake' : 'Send Intake Form')}
                    </ActionButton>
                    <ActionButton
                      variant="secondary"
                      onClick={() => window.open(`/admin/artists/${createdArtist.id}`, '_self')}
                      data-testid="ext-artist-open-profile"
                    >
                      Open Artist Profile
                    </ActionButton>
                  </div>
                  {intakeLink && (
                    <div className="border border-[#222] bg-[#0a0a0a] p-2.5">
                      <p className="text-[10px] font-mono text-[#aaa] break-all">{intakeLink}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
